import { supabase } from "../utils/supabase";
import { detectGameType } from "../utils/gameTypeUtils";
import { detectPairType } from "../utils/pairTypeDetector";
import { computeSinglesEloResults } from "../utils/singlesEloCalculation";
import { getSinglesEloForComputation } from "../utils/playerUtils";

/**
 * Insert ELO history and update player ELO atomically using the database function.
 * @param {Object} record - { player_id, club_id, session_id, season_id, pair_type, game_type_id, new_value, session_game_id }
 * @param {boolean} testMode - If true, disable fallback to test Edge Function directly
 * @returns {Promise<Object>} - The result from the database function (old_value, new_value, elo_change, etc.)
 */
/**
 * Apply ELO for one game via authorized batch RPC (session host / manager only).
 * @deprecated Prefer complete-session-score EF; kept for legacy callers.
 */
export const insertEloHistoryAtomic = async (record, testMode = false) => {
  try {
    if (!record.season_id) {
      throw new Error('season_id is required for ELO update');
    }
    if (!record.session_game_id) {
      throw new Error('session_game_id is required for ELO update');
    }

    const { data, error } = await supabase.rpc('apply_session_game_elo_batch', {
      p_session_game_id: record.session_game_id,
      p_updates: [
        {
          player_id: record.player_id,
          club_id: record.club_id,
          session_id: record.session_id,
          season_id: record.season_id,
          pair_type: record.pair_type,
          game_type_id: record.game_type_id,
          new_value: record.new_value,
        },
      ],
    });

    if (error) {
      console.error('[ELO] apply_session_game_elo_batch failed:', error);
      throw error;
    }

    if (!data?.ok) {
      throw new Error(data?.code || 'ELO batch apply failed');
    }

    const delta = data.elo_deltas?.[record.player_id];
    const eloChange = typeof delta?.total === 'number' ? delta.total : 0;

    if (testMode) {
      console.log('[ELO] Batch apply succeeded:', data);
    }

    return [{ old_value: null, new_value: record.new_value, elo_change: eloChange }];
  } catch (error) {
    console.error('[ELO] Error in insertEloHistoryAtomic:', error);
    throw error;
  }
};

/**
 * Create ELO history records with final ELO values for update_player_elo_and_history.
 * Singles (types 4, 5, 7): head-to-head singles_elo_rating via computeSinglesEloResults; pair_type 'singles'.
 * Doubles: unchanged mixed/levels logic.
 *
 * @param {Object|null} scoreContext - { team1Score, team2Score, winningTeam } (required for rated singles 4/5)
 * @returns {{ records, preGameTeam1Elo, preGameTeam2Elo, singlesRatingUpdates?: Record<string, { newRating: number, delta: number }> }}
 */
export const createFinalEloHistoryRecords = (
  players,
  gameId,
  sessionId,
  clubId,
  team1Ids,
  team2Ids,
  playersById,
  seasonId,
  preGameElos = {},
  scoreContext = null,
  eloExempt = false
) => {
  const records = [];
  const allPlayerIds = [...team1Ids, ...team2Ids];

  const team1Objs = team1Ids.map((id) => playersById[id]).filter(Boolean);
  const team2Objs = team2Ids.map((id) => playersById[id]).filter(Boolean);
  const gameType = detectGameType(team1Objs, team2Objs);
  const gameTypeId = gameType.id;

  const isSinglesMatch =
    [4, 5, 7].includes(gameTypeId) && team1Ids.length === 1 && team2Ids.length === 1;

  if (isSinglesMatch) {
    const p1 = team1Objs[0];
    const p2 = team2Objs[0];
    if (!p1 || !p2) {
      console.error("[ELO] Singles game missing player objects");
      return { records: [], preGameTeam1Elo: 0, preGameTeam2Elo: 0, singlesRatingUpdates: {} };
    }

    const team1Score = scoreContext?.team1Score;
    const team2Score = scoreContext?.team2Score;
    let team1Won;
    if (scoreContext?.winningTeam === 1) team1Won = true;
    else if (scoreContext?.winningTeam === 2) team1Won = false;
    else if (Number(team1Score) !== Number(team2Score)) {
      team1Won = Number(team1Score) > Number(team2Score);
    } else {
      console.error("[ELO] Singles game missing winner/score context; defaulting team1Won=true");
      team1Won = true;
    }

    const singlesRatingUpdates = computeSinglesEloResults(p1, p2, {
      team1Won,
      team1Score,
      team2Score,
      gameTypeId,
    });

    const pre1 = getSinglesEloForComputation(p1);
    const pre2 = getSinglesEloForComputation(p2);

    allPlayerIds.forEach((playerId) => {
      const u = singlesRatingUpdates[playerId];
      if (!u) return;
      records.push({
        player_id: playerId,
        club_id: clubId,
        session_game_id: gameId,
        session_id: sessionId,
        season_id: seasonId,
        new_value: Math.round(u.newRating),
        pair_type: "singles",
        game_type_id: gameTypeId,
      });
    });

    return {
      records,
      preGameTeam1Elo: Math.round(pre1),
      preGameTeam2Elo: Math.round(pre2),
      singlesRatingUpdates,
    };
  }

  const fallbackPairTypeForRatings = (typeId) => {
    if (typeId === 1) return "mixed";
    if ([2, 3, 6].includes(typeId)) return "levels";
    if ([4, 5, 7].includes(typeId)) return "singles";
    return "levels";
  };

  const pairTypeTeam1 = detectPairType(team1Objs, gameTypeId);
  const pairTypeTeam2 = detectPairType(team2Objs, gameTypeId);
  const ratingPairTypeTeam1 = pairTypeTeam1 || fallbackPairTypeForRatings(gameTypeId);
  const ratingPairTypeTeam2 = pairTypeTeam2 || fallbackPairTypeForRatings(gameTypeId);

  const team1PreGameElos = team1Ids.map((id) => {
    const player = playersById[id];
    return ratingPairTypeTeam1 === "mixed"
      ? player.mixed_elo_rating
      : player.levels_elo_rating;
  });
  const team2PreGameElos = team2Ids.map((id) => {
    const player = playersById[id];
    return ratingPairTypeTeam2 === "mixed"
      ? player.mixed_elo_rating
      : player.levels_elo_rating;
  });
  const preGameTeam1Elo = Math.round(
    team1PreGameElos.reduce((a, b) => a + b, 0) / team1PreGameElos.length
  );
  const preGameTeam2Elo = Math.round(
    team2PreGameElos.reduce((a, b) => a + b, 0) / team2PreGameElos.length
  );

  allPlayerIds.forEach((playerId) => {
    const player = playersById[playerId];
    if (!player) {
      console.warn(`Player not found for ID: ${playerId}`);
      return;
    }

    const isTeam1 = team1Ids.includes(playerId);
    const playerTeamIds = isTeam1 ? team1Ids : team2Ids;
    const playerTeamObjs = playerTeamIds.map((id) => playersById[id]).filter(Boolean);
    const playerPairType = detectPairType(playerTeamObjs, gameTypeId);
    const ratingPairType = playerPairType || fallbackPairTypeForRatings(gameTypeId);

    if (!playerPairType && gameTypeId !== 99) {
      console.warn("[ELO] Unable to determine pair_type for player", {
        gameTypeId,
        playerId,
        teamPlayers: playerTeamObjs.map((p) => ({ id: p?.id, gender: p?.gender })),
      });
    }

    let finalElo;
    if (gameTypeId === 6 || eloExempt) {
      // Unbalanced games and session mens doubles (ELO-exempt): freeze at pre-game values
      const preGameElo = preGameElos[playerId];
      if (preGameElo !== undefined) {
        finalElo = preGameElo;
      } else {
        console.warn(
          `[ELO] Pre-game ELO not found for player ${playerId} in ${eloExempt ? 'ELO-exempt' : 'unbalanced'} game, using current ELO`
        );
        finalElo = ratingPairType === 'mixed' ? player.mixed_elo_rating : player.levels_elo_rating;
      }
    } else {
      finalElo =
        ratingPairType === "mixed"
          ? player.mixed_elo_rating
          : player.levels_elo_rating;
    }

    records.push({
      player_id: playerId,
      club_id: clubId,
      session_game_id: gameId,
      session_id: sessionId,
      season_id: seasonId,
      new_value: finalElo,
      pair_type: playerPairType,
      game_type_id: gameTypeId,
    });
  });

  return { records, preGameTeam1Elo, preGameTeam2Elo, singlesRatingUpdates: undefined };
};

export const clearEloHistoryQueue = () => {};

export const getEloHistoryQueueStatus = () => ({
  queueLength: 0,
  isProcessing: false,
});
